import { findLastInStackSafe, getTimerDistance, selectDisplayedOrAllGroupQuestions } from 'components-machine';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { Poll } from 'schema/poll';
import PageTimer from '../../components/PageTimer';
import { usePollStatePredicate } from '../../reducer/machine/utils';
import { ICState } from '../../types/domain';
import { Poll as PollType } from '../../types/poll.domain';
import { getTimerHeight } from '../../utils/notifyIframeHeight';
import { useTimerInfo } from './hooks/useTimerInfo';

export function PollTimer({ children, state, poll }: { children: ReactNode; state: ICState; poll: PollType }) {
  const timerInfo = useTimerInfo({ poll, state });

  const isFinishedPoll = usePollStatePredicate('finish');

  const timerStrict = !!timerInfo?.timerStrict;
  const isGlobalTimer = !!poll.hasTimer;

  const timerCreatedAt = isGlobalTimer ? state.startedAt : getTimerCreatedAt(state, poll);
  const hasTimeExpired = useTimeExpired(timerCreatedAt, timerInfo?.timerDuration);

  const [timerHeight, setTimerHeight] = useState(() => getTimerHeight());

  const timerIsShown = !isFinishedPoll && !!timerCreatedAt && !!timerInfo?.timerDuration;

  const timerContextValue: TimerContextValue = useMemo(
    () => ({
      timerIsShown,
      timerHeight: timerIsShown ? timerHeight : 0,
      timerStrict,
      isGlobalTimer,
      hasTimeExpired,
    }),
    [timerIsShown, timerHeight, timerStrict, isGlobalTimer, hasTimeExpired],
  );

  if (!timerIsShown) return <TimerContext.Provider value={timerContextValue}>{children}</TimerContext.Provider>;

  return (
    <TimerContext.Provider value={timerContextValue}>
      <PageTimer
        key={isGlobalTimer ? 'static' : state.groupId || state.questionId}
        timerInfo={timerInfo}
        updateTimerHeight={setTimerHeight}
        createdAt={timerCreatedAt}
      />
      {children}
    </TimerContext.Provider>
  );
}

export type TimerContextValue = {
  hasTimeExpired: boolean;
  timerHeight: number;
  timerIsShown: boolean;
  isGlobalTimer: boolean;
  timerStrict: boolean;
};

const TimerContext = React.createContext<TimerContextValue | undefined>(undefined);

export function useTimerContext() {
  const value = useContext(TimerContext);
  if (!value) throw new Error('Timer context is required!');
  return value;
}

function useTimeExpired(createdAt: number | undefined, duration: number | undefined) {
  const startedAt = createdAt || new Date().valueOf();
  const [, setIsFinished] = useState(false);

  useEffect(() => {
    setIsFinished(false);
    const intervalId = setTimeout(
      () => {
        setIsFinished(true);
      },
      getTimerDistance(startedAt, duration) * 1000,
    );
    return () => {
      clearInterval(intervalId);
    };
  }, [startedAt, duration]);

  if (duration && createdAt) return getTimerDistance(createdAt, duration) <= 0;
  return false;
}

function getTimerCreatedAt(state: ICState, poll: Poll) {
  if (state.groupId) {
    const questions = selectDisplayedOrAllGroupQuestions(poll, state.groupId, state.answersStack);

    for (const question of questions) {
      const found = findLastInStackSafe(state.answersStack, question.id);
      if (found && found.createdAt) return found.createdAt;
    }
  } else if (state.questionId) {
    const found = findLastInStackSafe(state.answersStack, state.questionId);
    if (found && found.createdAt) return found.createdAt;
  }

  return undefined;
}
